import { Alert, Box, Button, Chip, debounce, Divider, IconButton, Stack, TextField, Typography } from '@mui/material';
import { BubbleMenu, Editor, EditorContent, useEditor } from '@tiptap/react';
import './RichTextEditor.css';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Localized } from '@fluent/react';
import CloseIcon from '@mui/icons-material/Close';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import RemoveDoneIcon from '@mui/icons-material/RemoveDone';
import SendIcon from '@mui/icons-material/Send';
import DeleteIcon from '@mui/icons-material/Delete';
import ReplayIcon from '@mui/icons-material/Replay';

//TODO: incorporate tiptap-extension-resizable-image into cobdebase and remove from package.json
import 'tiptap-extension-resizable-image/styles.css';
import BubbleMenuExt from '@tiptap/extension-bubble-menu';
import Table from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableHeader from '@tiptap/extension-table-header';
import TableCell from '@tiptap/extension-table-cell';
import { AppComment, collectCommentRanges } from './tiptapComments';
import { nanoid, unwrapResult } from '@reduxjs/toolkit';
import { LoadingButton } from '@mui/lab';
import { APIError, AppId } from './appTypes';
import { ServerErrorMsg } from './ServerErrorMsg';
import { formatDate, formatTime, impossible } from './Utils';
import { useAppDispatch, useAppSelector } from './hooks';
import { selectLoggedUser } from '../features/user/userSlice';
import { CommentStatus } from '../features/urs/URSCommentSlice';
import StarterKit from '@tiptap/starter-kit';
import { blocksSelector, isDocEditable, selectDocument } from '../features/documents/documentSlice';
import { store } from './store';
import { updateDocBlock } from '../features/documents/documentsApi';
import { RichTextContainer } from './RichTextEditor';

export interface TiptapPos {
    from: number
    to: number
}
export interface CommentCoord {
    id: string
    pos: TiptapPos | null
}
export interface TiptapComment {
    id: string
    content: string
    author: string
    authorFullName: string
    created: Date
    // creationDate: string
    // creationTime: string
    new: boolean
    pos: TiptapPos | null
    quot: string
    replays: TiptapComment[]
    externalId?: string
    status?: CommentStatus
}
export interface TextWithCommentsProps {
    sectionId: AppId
    comments: TiptapComment[]
    requestComment: (id: string, from: number, to: number) => void
    createComment: (content: string, pos: TiptapPos | null, parentCommentId: string | null) => Promise<string>
    updateComment: (id: string, content: string) => Promise<void>
    deleteComment: (id: AppId) => Promise<void>
    resolveComment: (id: AppId) => Promise<void>
    unresolveComment: (id: AppId) => Promise<void>
    reply: (content: string) => Promise<void>
    isSendingEnabled: boolean
    isResolvingEnabled: boolean
}
interface DraftComment {
    id: string
    content: string
    pos: TiptapPos
}

export const TextWithComments = (props: TextWithCommentsProps) => {
    const { comments, createComment, updateComment, sectionId, isSendingEnabled, isResolvingEnabled } = props
    const dispatch = useAppDispatch()
    const { header } = useAppSelector(selectDocument).doc ?? impossible()
    const { id: docId } = header
    const isEditable = useAppSelector(isDocEditable)

    const [activeComment, setActiveComment] = useState<CommentCoord | null>(null)
    const [draft, setDraft] = useState<DraftComment | null>(null)
    const [mainComment, setMainComment] = useState('')
    const [mainErr, setMainErr] = useState<APIError | null>(null)
    const [saving, setSaving] = useState(false)
    const isSettingContent = useRef(false)

    const commentsRef = useRef(comments)
    useEffect(() => { commentsRef.current = comments }, [comments])

    const autosave = useCallback(debounce((ed: Editor) => {
        if (!isEditable) return

        // the marks moved with the text as it was edited; bring the stored anchors back in line
        const anchored = collectCommentRanges(ed.state.doc)
        const moved: { commentId: AppId, pos: TiptapPos | null }[] = []
        for (const c of commentsRef.current) {
            const range = anchored.get(c.id)
            if (range) {
                if (c.pos === null || c.pos.from !== range.from || c.pos.to !== range.to) {
                    moved.push({ commentId: c.id, pos: { from: range.from, to: range.to } })
                }
            } else if (c.pos !== null) {
                // the commented text is gone - unanchor it rather than leave a stale position that
                // would re-attach the comment to whatever now sits at those offsets
                moved.push({ commentId: c.id, pos: null })
            }
        }
        // one request, one transaction: the content and its anchors are saved together or not at all
        dispatch(updateDocBlock({
            sectionId,
            docId,
            description: ed.getHTML(),
            positions: moved,
        }))
            .then(unwrapResult)
            .catch(err => console.log('Error: ', err))
    }, 300), [sectionId, docId, isEditable])

    const onCommentActivated = (commentID: string | null) => {
        // setActiveComment(comments.find(x => x.id === commentID) ?? null)
    }

    const editor = useEditor({
        extensions:
            [ StarterKit.configure({ heading: { levels: [1,2,3] } })
            , BubbleMenuExt.configure({
                shouldShow: ({ editor, view, state, oldState, from, to }) => {
                    // this function doesn't work (bug in github)
                    return true // editor.isActive('image') || editor.isActive('link')
                },
            })
            , AppComment.configure({
                HTMLAttributes: {
                    class: 'app-comment',
                },
                onCommentActivated,
            })
            , Table.configure({
                resizable: false
            })
            , TableRow
            , TableHeader
            , TableCell
        ],
        editable: true,
        onUpdate: e => { if (!isSettingContent.current) autosave(e.editor) },
        editorProps: {
            handleKeyDown: () => !isEditable,
            handlePaste: () => !isEditable,
            handleDrop: () => !isEditable,
        },
        content: '',
        //TODO: remap comment positions later
        // onTransaction: ({ transaction: tr }) => {
        //     if (!tr.docChanged || isSettingContent.current) return
        //     setLocalComments(prev => prev.map(c => {
        //         if (c.pos === null) return c
        //         const newFrom = tr.mapping.map(c.pos.from)
        //         const newTo   = tr.mapping.map(c.pos.to)
        //         return { ...c, pos: newFrom === newTo ? null : { from: newFrom, to: newTo } }
        //     }))
        // },
    })

    useEffect(() => {
        if (editor) {
            isSettingContent.current = true
            const content = blocksSelector.selectById(store.getState(), sectionId)?.description ?? ''
            // Loading is not an edit: kept out of the undo history, or undo would blank the section
            // (or strip the anchors fixed below) and autosave that.
            editor.chain().setMeta('addToHistory', false).setContent(content).run()
            const repair = editor.chain().setMeta('addToHistory', false)
            // marks restored from the saved content win: they were mapped through every edit, so they
            // still sit on the right text. c.pos is frozen at creation time and is only a fallback for
            // comments that have no mark yet - created while the document was not editable, so the
            // content was never saved with them.
            const anchored = collectCommentRanges(editor.state.doc)
            for (const c of comments) {
                if (c.pos !== null && !anchored.has(c.id)) {
                    repair.setTextSelection({ from: c.pos.from, to: c.pos.to })
                        .setMark('comment', { commentId: c.id })
                }
            }
            // a deleted comment leaves its mark behind in the saved content; drop those so they
            // stop rendering as an underline nothing in the panel corresponds to. Safe to compare
            // against `comments` here: loadDocComments is awaited before any section renders.
            const known = new Set(comments.map(c => c.id))
            for (const id of anchored.keys()) {
                if (!known.has(id)) {
                    repair.unsetComment(id)
                }
            }
            repair.run()
            isSettingContent.current = false
            // setContent above drops the active-comment decorations; a stale id would also block re-clicking that comment
            setActiveComment(null)
        }
    }, [editor, sectionId])

    useEffect(() => {
        if (editor) {
            editor.setOptions({
                onUpdate: e => { if (!isSettingContent.current) autosave(e.editor) },
                editorProps: {
                    handleKeyDown: () => !isEditable,
                    handlePaste: () => !isEditable,
                    handleDrop: () => !isEditable,
                },
            })
        }
    }, [autosave])

    const handleAddCommentClick = () => {
        if (editor && isSendingEnabled) {
            // starting a second draft abandons the first one, whose mark would otherwise stay behind
            if (draft) editor.commands.unsetComment(draft.id)
            const commentId = nanoid()
            editor.commands.setComment(commentId)
            const { $from, $to } = editor.state.selection
            // requestComment(commentId, $from.pos, $to.pos)
            const pos = {
                from: $from.pos,
                to: $to.pos,
            }
            setDraft({
                id: commentId,
                content: '',
                pos,
            })
            setActiveComment({
                id: commentId,
                pos,
            })
        }
    }
    const handleSetActiveComment = (c: CommentCoord | null) => {
        setActiveComment(c)
    }

    useEffect(() => {
        if (editor) {
            editor.commands.setActiveComment(activeComment?.id ?? null)
        }
    }, [activeComment, editor])

    const handlePostMainCommentClick = (event) => {
        if (event.key === 'Enter' && mainComment) {
            setSaving((true))
            createComment(mainComment, null, null)
                .then(() => {
                    setMainComment('')
                })
                .catch(err => {
                    setMainErr(err as APIError)
                })
                .finally(() => {
                    setSaving(false)
                })
        }
    }
    const handleSendMainCommentClick = () => {
        if (mainComment) {
            setSaving((true))
            createComment(mainComment, null, null)
                .then(() => {
                    setMainComment('')
                })
                .catch(err => {
                    setMainErr(err as APIError)
                })
                .finally(() => {
                    setSaving(false)
                })
        }
    }
    const cancel = useCallback(() => {
        // the mark went on optimistically when the bubble button was clicked - take it back out
        if (draft) editor?.commands.unsetComment(draft.id)
        setDraft(null)
        setActiveComment(null)
    }, [draft, editor])

    const deleteCommentWithMark = useCallback(async (id: AppId) => {
        await props.deleteComment(id)
        editor?.commands.unsetComment(id)
        // holding on to the id of a comment that no longer exists keeps the highlight alive
        setActiveComment(prev => prev?.id === id ? null : prev)
    }, [editor, props.deleteComment])

    const createCommentWithMark = useCallback(async (msg: string, pos: TiptapPos | null) => {
        const newCommentId = await createComment(msg, pos, null)
        if (draft) {
            // the mark was tagged with a locally generated id; align it with the id the comment got on the server
            editor?.commands.retagComment(draft.id, newCommentId)
            setDraft(null);
            setActiveComment(null);
        }
    }, [draft, editor])

    return <Box sx={{
        display: 'grid',
        gridTemplateColumns: '1fr 400px',
        gap: 1,
    }}>
        <Box sx={{
            border: '1px solid var(--gray-200)',
            p: 1,
            // maxHeight: '400px',
            overflow: 'auto',
        }}>
            {editor && !editor.isDestroyed && (
                <BubbleMenu
                    editor={editor}
                    shouldShow={({ editor, state, from, to }) => {
                        return isSendingEnabled && editor.isFocused && !state.selection.empty && from < to
                    }}
                >
                    <div className="bubble-menu">
                        <button
                            type="button"
                            onClick={handleAddCommentClick}
                            disabled={!isSendingEnabled}
                        >
                            Dodaj komentarz
                        </button>
                    </div>
                </BubbleMenu>
            )}
            {isEditable ? <RichTextContainer label={undefined} editor={editor} actions={<></>} /> : <EditorContent editor={editor} />}
        </Box>
        <Box sx={{
            border: '1px solid var(--gray-200)',
            p: 1,
            // maxHeight: '400px',
            overflow: 'auto',
            gridRow: 'span 2 / span 2',
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
        }}>
            {comments.length === 0 && <Alert severity='info'>
                <Typography variant='caption'>
                    Brak komentarzy. Aby dodać komentarz wprowadź go w pole poniżej lub
                    zanaczając fragment treści wymagania kliknij przycisk 'Dodaj komentarz'.
                </Typography>
            </Alert>
            }
            <CommentsPanel
                comments={comments}
                draft={draft}
                activeComment={activeComment}
                setActiveComment={handleSetActiveComment}
                createComment={createCommentWithMark}
                reply={createComment}
                updateComment={updateComment}
                deleteComment={deleteCommentWithMark}
                resolveComment={props.resolveComment}
                unresolveComment={props.unresolveComment}
                cancel={cancel}
                isSendingEnabled={isSendingEnabled}
                isResolvingEnabled={isResolvingEnabled}
            />
            <Divider />
            <ServerErrorMsg err={mainErr} />
            {isSendingEnabled && <TextField
                value={mainComment}
                disabled={saving}
                onChange={e => setMainComment(e.target.value)}
                onKeyDown={handlePostMainCommentClick}
                placeholder="Komentarz..."
                fullWidth
                multiline
            />}
            {isSendingEnabled && <LoadingButton loading={saving} variant='outlined' startIcon={<SendIcon />} onClick={handleSendMainCommentClick} size="small">
                Wyślij
            </LoadingButton>}
        </Box>
        <Box>
            <Stack direction="row" spacing={1}>
                {/* <LoadingButton loading={saving} variant='outlined' startIcon={<SendIcon />} onClick={handlePostMainCommentClick} size="large"> */}
                {/*     Wyślij */}
                {/* </LoadingButton> */}
            </Stack>
        </Box>
    </Box>
}

export const JustComments = (props: TextWithCommentsProps) => {
    const { comments, requestComment, createComment, updateComment, isSendingEnabled, isResolvingEnabled } = props

    const [activeComment, setActiveComment] = useState<CommentCoord | null>(null)
    const [draft, setDraft] = useState<DraftComment | null>(null)
    const [mainComment, setMainComment] = useState('')
    const [mainErr, setMainErr] = useState<APIError | null>(null)
    const [saving, setSaving] = useState(false)

    const onCommentActivated = (commentID: string | null) => {
        // setActiveComment(comments.find(x => x.id === commentID) ?? null)
    }

    const handlePostMainCommentEnter = (event) => {
        if (event.key === 'Enter' && mainComment) {
            setSaving((true))
            createComment(mainComment, null, null)
                .then(() => {
                    setMainComment('')
                })
                .catch(err => {
                    setMainErr(err as APIError)
                })
                .finally(() => {
                    setSaving(false)
                })
        }
    }

    const handlePostMainCommentClick = (event) => {
        if (mainComment) {
            setSaving((true))
            createComment(mainComment, null, null)
                .then(() => {
                    setMainComment('')
                })
                .catch(err => {
                    setMainErr(err as APIError)
                })
                .finally(() => {
                    setSaving(false)
                })
        }
    }

    const cancel = useCallback(() => {
        setDraft(null)
        setActiveComment(null)
    }, [])

    const createCommentWithMark = useCallback(async (msg: string, pos: TiptapPos | null) => {
        await createComment(msg, pos, null)
        if (draft) {
            setDraft(null);
            setActiveComment(null);
        }
    }, [draft])

    const handleSetActiveComment = (c: CommentCoord | null) => {
        setActiveComment(c)
    }

    return <Box sx={{ }}>
        <Typography component="div" variant="subtitle2">
            <Localized id='comments'>Komentarze</Localized>:
        </Typography>
        <Box sx={{
            border: '1px solid var(--gray-200)',
            p: 1,
            maxHeight: '400px',
            overflow: 'auto',
            gridRow: 'span 2 / span 2',
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
        }}>
            {comments.length === 0 && <Alert severity='info'>
                <Typography variant='caption'>
                    Brak komentarzy
                </Typography>
            </Alert>}
            <CommentsPanel
                comments={comments}
                draft={draft}
                activeComment={activeComment}
                setActiveComment={handleSetActiveComment}
                createComment={createCommentWithMark}
                reply={createComment}
                updateComment={updateComment}
                deleteComment={props.deleteComment}
                resolveComment={props.resolveComment}
                unresolveComment={props.unresolveComment}
                cancel={cancel}
                isSendingEnabled={isSendingEnabled}
                isResolvingEnabled={isResolvingEnabled}
            />
            <Divider />
            <ServerErrorMsg err={mainErr} />
            {isSendingEnabled && <TextField
                value={mainComment}
                disabled={saving}
                onChange={e => setMainComment(e.target.value)}
                onKeyDown={handlePostMainCommentEnter}
                placeholder="Komentarz..."
                fullWidth
                multiline
            />}
            {isSendingEnabled && <LoadingButton loading={saving} variant='outlined' startIcon={<SendIcon />} onClick={handlePostMainCommentClick} size="small">
                Wyślij
            </LoadingButton>}
        </Box>
    </Box>
}

interface CommentsPanelProps {
    comments: TiptapComment[]
    draft: DraftComment | null
    activeComment: CommentCoord | null
    setActiveComment: (c: CommentCoord | null) => void
    createComment: (msg: string, pos: TiptapPos | null, parentCommentId: string | null) => Promise<void>
    reply: (msg: string, pos: TiptapPos | null, parentCommentId: string | null) => Promise<string>
    updateComment: (id: string, content: string) => Promise<void>
    deleteComment: (id: AppId) => Promise<void>
    resolveComment: (id: AppId) => Promise<void>
    unresolveComment: (id: AppId) => Promise<void>
    cancel: () => void
    isSendingEnabled: boolean
    isResolvingEnabled: boolean
}
const borderGray = '1px solid var(--gray-200)'
const borderActive = '2px solid var(--blue-400)'
const CommentsPanel = (props: CommentsPanelProps) => {
    const { comments, createComment, reply, updateComment, activeComment, setActiveComment, draft, isSendingEnabled, isResolvingEnabled } = props

    const handleCommentClick = useCallback(({ id, pos }: TiptapComment) => {
        props.setActiveComment({
            id,
            pos,
        })
    }, [])

    return <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
    }}>
        {draft !== null && <Box sx={{
            border: borderActive,
            borderRadius: 2,
            p: 2,
        }}>
            <NewComment
                createComment={createComment}
                draft={draft}
                cancel={props.cancel}
            />
        </Box>}
        {comments.map(c => <Box sx={{
            border: c.id === activeComment?.id ? borderActive : borderGray,
            borderRadius: 2,
            p: 2,
        }} onClick={() => c.id !== activeComment?.id &&  handleCommentClick(c)}>
            <PersistedComment
                key={c.id}
                comment={c}
                reply={reply}
                updateComment={updateComment}
                deleteComment={props.deleteComment}
                setActiveComment={setActiveComment}
                resolveComment={props.resolveComment}
                unresolveComment={props.unresolveComment}
                active={c.id === activeComment?.id}
                isSendingEnabled={isSendingEnabled}
                isResolvingEnabled={isResolvingEnabled}
            />
        </Box>)}
    </Box>
}

interface CommentProps {
    comment: TiptapComment
    reply: (msg: string, pos: TiptapPos | null, parentCommentId: string | null) => Promise<string>
    updateComment: (id: string, content: string) => Promise<void>
    deleteComment: (id: AppId) => Promise<void>
    setActiveComment: (c: CommentCoord | null) => void
    resolveComment: (id: AppId) => Promise<void>
    unresolveComment: (id: AppId) => Promise<void>
    active: boolean
    isSendingEnabled: boolean
    isResolvingEnabled: boolean
}

interface NewCommentProps {
    createComment: (id: string, pos: TiptapPos | null, parentCommentId: string | null) => Promise<void>
    draft: DraftComment
    cancel: () => void
}
const NewComment = (props: NewCommentProps) => {
    const { createComment, draft } = props

    const [content, setContent] = useState('')
    const [isSaving, setIsSaving] = useState(false)
    const [err, setErr] = useState<APIError | null>(null)

    const handleSaveClick = () => {
        if (content) {
            setIsSaving(true)
            createComment(content, draft.pos, null)
                .catch(e => {
                    setErr(e as APIError)
                })
                .finally(() => setIsSaving(false))
        }
    }

    return <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
    }}>
        <ServerErrorMsg err={err} />
        <TextField
            focused
            multiline
            fullWidth
            value={content}
            onChange={e => setContent(e.target.value)}
        />
        <Box sx={{
            display: 'flex',
            gap: .5,
        }}>
            <LoadingButton loading={isSaving} variant='outlined' size='small' onClick={handleSaveClick}>
                <Localized id='save'>Zapisz</Localized>
            </LoadingButton>
            <LoadingButton loading={isSaving} variant='outlined' size='small' color='error' onClick={props.cancel}>
                <Localized id='cancel'>Anuluj</Localized>
            </LoadingButton>
        </Box>
    </Box>
}

const PersistedComment = (props: CommentProps) => {
    const { comment, active, setActiveComment, reply, updateComment, deleteComment, isSendingEnabled, isResolvingEnabled } = props
    const currentUser = useAppSelector(selectLoggedUser)
    const { replays, status } = comment
    const [isEditing, setIsEditing] = useState(false)
    const [isSaving, setIsSaving] = useState(false)
    const [editContent, setEditContent] = useState(comment.content)
    const [replyText, setReplyText] = useState('')

    const handleSave = useCallback(() => {
        setIsSaving(true)
        updateComment(comment.id, editContent)
            .then(() => {
                setIsEditing(false)
            })
            .finally(() => {
                setIsSaving(false)
            })
    }, [comment.id, editContent, updateComment])

    const handleCancel = useCallback(() => {
        setEditContent(comment.content)
        setIsEditing(false)
    }, [comment.content])

    const handleEdit = useCallback(() => {
        setIsEditing(true)
    }, [])

    const handleClose = useCallback((e: React.MouseEvent) => {
        e.stopPropagation()
        setActiveComment(null)
    }, [setActiveComment])

    const handleContentChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setEditContent(e.target.value)
    }, [])

    const handleDeleteClick = () => {
        setIsSaving((true))
        deleteComment(comment.id)
    }

    const handleEnterReply = (event) => {
        if (event.key === 'Enter' && replyText) {
            setIsSaving(true)
            reply(replyText, null, comment.id)
                .then(() => {
                    setReplyText('')
                })
                .catch(err => console.log('Error: ', err))
                .finally(() => setIsSaving(false))
        }
    }

    const handleReplyClick = () => {
        if (replyText) {
            setIsSaving(true)
            reply(replyText, null, comment.id)
            .then(() => {
                setReplyText('')
            })
            .catch(err => console.log('Error: ', err))
            .finally(() => setIsSaving(false))
        }
    }

    const handleResolveClick = useCallback(() => {
        props.resolveComment(comment.id)
    }, [])
    const handleUnresolveClick = useCallback(() => {
        props.unresolveComment(comment.id)
    }, [])

    const replyCount = comment.replays.length
    const canEdit = isSendingEnabled && replyCount === 0 && comment.author === currentUser

    return <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
    }}>
        {active && <Box sx={{
            display: 'flex',
        }}>
            {isResolvingEnabled && <IconButton size='small' onClick={handleResolveClick}><DoneAllIcon /></IconButton>}
            {isResolvingEnabled && <IconButton size='small' onClick={handleUnresolveClick}><RemoveDoneIcon /></IconButton>}
            <IconButton sx={{ ml: 'auto' }} size='small' onClick={handleClose}><CloseIcon /></IconButton>
        </Box>}
        {active && <Divider />}
        <Typography component='div' variant='body2' gutterBottom>
            <CommentHead comment={comment} />
            {isEditing ? (
                <Box sx={{ mt: 1 }}>
                    <TextField
                        fullWidth
                        multiline
                        value={editContent}
                        onChange={handleContentChange}
                    />
                    <Box sx={{ display: 'flex', gap: .5, mt: 1 }}>
                        <LoadingButton loading={isSaving} size='small' variant='outlined' onClick={handleSave}>
                            <Localized id='save'>Zapisz</Localized>
                        </LoadingButton>
                        <LoadingButton loading={isSaving} size='small' variant='outlined' color='error' onClick={handleCancel}>
                            <Localized id='cancel'>Anuluj</Localized>
                        </LoadingButton>
                    </Box>
                </Box>
            ) : (
                <Box>{comment.content}</Box>
            )}
        </Typography>
        {active && !isEditing && <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
        }}>
            {canEdit && <Box sx={{ display: 'flex', gap: .5, }}>
                <Button size='small' variant='outlined' onClick={handleEdit}>
                    <Localized id='edit'>Edutuj</Localized>
                </Button>
                <Button startIcon={<DeleteIcon />} size='small' variant='outlined' color='error' onClick={handleDeleteClick}>
                    <Localized id='delete'>Usuń</Localized>
                </Button>
            </Box>}
        </Box>}
        {replyCount === 0
            ? <Typography sx={{ mt: .5, }} component='div' color='text.secondary' variant='caption' gutterBottom>
                {replyCount} odpowiedzi
              </Typography>
            : <Box>
                <Chip
                    size="small"
                    icon={<ReplayIcon />}
                    label={`${replyCount} odpowiedzi`}
                    color="primary"
                    variant="outlined"
                    sx={{ mt: 0.5, px: .5 }}
                />
            </Box>
        }
        {active && <Divider />}
        {active && replays.length > 0 && <Box sx={{
            background: 'var(--slate-50)',
            display: 'flex',
            flexDirection: 'column',
            gap: .25,
            p: 1,
        }}>
            {replays.map(msg => <Typography key={msg.id} sx={{
                border: '1px solid var(--slate-400)',
                background: 'var(--slate-200)',
                borderRadius: 2,
                p: 1,
                display: 'flex',
                flexDirection: 'column',
            }} component='div' variant='body2' gutterBottom>
                <CommentHead comment={msg} />
                <Box>{msg.content}</Box>
            </Typography>)}
        </Box>}
        {/* {active && replays.length > 0 && <Divider />} */}
        {active && 
            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: .25,
            }}>
                <TextField
                    disabled={isSaving}
                    size='small'
                    placeholder='Odpowiedź...'
                    value={replyText}
                    onChange={e => setReplyText(e.target.value)}
                    onKeyDown={handleEnterReply} />
                <Button
                    startIcon={<ReplayIcon />}
                    variant='outlined'
                    onClick={handleReplyClick}
                    size='small'>Odpowiedz</Button>
            </Box>
        }
    </Box>
}

const CommentHead = (props: { comment: TiptapComment }) => {
    const { comment }  = props
    const { authorFullName, created, status } = comment
    const creationDate = formatDate(created, false)
    const creationTime = formatTime(created)
    return <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        [`& label`]: {
            fontWeight: 800,
        },
        [`& > span`]: {
            fontSize: '.8em',
            color: 'text.secondary',
        },
    }}>
        <label>{authorFullName}</label>
        <span>{creationDate}</span>
        <span>{creationTime}</span>
        {status && <CommentStatusLabel status={status} />}
    </Box>
}

const CommentStatusLabel = ({ status }: { status: CommentStatus }) => {
    const style = { height: '20px', ml: 'auto', fontSize: '.75em', fontWeight: 800 }
    switch (status) {
        case "N":
            return <Chip sx={style} size="small" color="info" label={<Localized id="new">Nowy</Localized>} />
        case "R":
            return <Chip sx={style} size="small" color="success" label={<Localized id="resolved">Rozwiązany</Localized>} />
        case "U":
            return <Chip sx={style} size="small" color="error" label={<Localized id="unresolved">Odrzucony</Localized>} />
    }
}
